import {store} from "../store";
import IControlBarTogglesEnabledState from "../../services/playbackOrchestrator/IControlBarTogglesEnabledState";
import DemoMode from "../../models/view/lessonPage/DemoMode";
import Lesson from "../../models/view/lessonPage/Lesson";
import IScoreModal from "../../models/view/lessonPage/IScoreModal";
import Segment from "../../models/view/lessonPage/Segment";
import ISelectOption from "../../models/view/common/ISelectOption";
import {lessonPlayerSlice, ILessonPlayerStore} from "./LessonPlayerSlice";
import {NotationLayoutEnum} from "../../models/view/lessonPage/NotationLayoutEnum";
import {microphoneStore} from "../microphone/microphoneStore";
import {settingsStore} from "../settings/settingsStore";
import {localStorageService} from "../../services/storage/local-storage";
import storageKeys from "../../services/storage/storageKeys";
import {arrayRemove, arrayUpsert} from "../../util/array-util";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {
  ILessonSpacing, IMinifiedLessonSpacing,
  IPlaybackControlState
} from "../../services/playbackOrchestrator/PlaybackControlState/IPlaybackControlStateTypes";
import {
  getUserPreferencesForControlBar,
  partiallySetUserPreferencesForControlBar,
} from "../../services/UserPreferencesForControlBarService";
import {maximumSafeZoomValue, minimumSafeZoomValue} from "../../services/soundSlice/soundSlice";

export const lessonPlayerStore = {
  // State modifiers
  incrementMetricsSegmentsOpenedCount: () => {
    const newCount = lessonPlayerStore.getMetricsSegmentsOpenedCount() + 1;
    lessonPlayerStore.setMetricsSegmentsOpenedCount(newCount);
  },
  setActiveLessonId: (lessonId: number | undefined) => {
    store.dispatch((lessonPlayerSlice.actions.activeLessonIdReducer({ lessonId })));
  },
  setAllLessonSpacing: (lessonSpacing: ILessonSpacing[]) => {
    const minifiedLessonSpacing = minifyLessonSpacing(lessonSpacing);
    localStorageService.setJsonItem<IMinifiedLessonSpacing[]>(storageKeys.lessonPlayer_notationSpacing, minifiedLessonSpacing);
    store.dispatch((lessonPlayerSlice.actions.allLessonSpacingReducer({ lessonSpacing })));
  },
  setAppliedNotationLayout: (layout: number) => {
    store.dispatch((lessonPlayerSlice.actions.appliedNotationLayoutReducer({ layout })));
    partiallySetUserPreferencesForControlBar({appliedNotationLayout: layout});
  },
  setAreNoteNamesVisible: (isVisible: boolean) => { store.dispatch((lessonPlayerSlice.actions.noteNamesVisibleReducer({ isVisible }))); },
  setAreStaffFingeringsVisible: (isVisible: boolean) => { store.dispatch((lessonPlayerSlice.actions.staffFingeringsVisibleReducer({ isVisible }))); },
  setCanShowDemoSoundActivatedPrompt: (canShow: boolean) => { store.dispatch((lessonPlayerSlice.actions.canShowDemoSoundActivatedPromptReducer({ canShow }))); },
  setCanShowKeyboardConnectedModal: (canShowKeyboardConnectedModal: boolean) => { store.dispatch((lessonPlayerSlice.actions.canShowKeyboardConnectedModalReducer({ canShowKeyboardConnectedModal }))); },
  setCanShowKeyboardDisconnectedModal: (canShowKeyboardDisconnectedModal: boolean) => { store.dispatch((lessonPlayerSlice.actions.canShowKeyboardDisconnectedModalReducer({ canShowKeyboardDisconnectedModal }))); },
  setCanShowPitchDetectionDisabledDueToDemoSoundEnabledMessage: (canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage: boolean) => { store.dispatch((lessonPlayerSlice.actions.canShowPitchDetectionDisabledDueToDemoSoundEnabledMessageReducer({ canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage: canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage }))); },
  setCanShowSwitchToPitchDetectionPrompt: (canShow: boolean) => { store.dispatch((lessonPlayerSlice.actions.canShowSwitchToPitchDetectionPromptReducer({ canShow }))); },
  setControlBarToggles: (controlBarTogglesEnabledState: IControlBarTogglesEnabledState) => { store.dispatch((lessonPlayerSlice.actions.controlBarTogglesEnabledStateReducer({ controlBarTogglesEnabledState }))); },
  setDemoMode: (demoMode: DemoMode) => {
    store.dispatch((lessonPlayerSlice.actions.demoModeReducer({ demoMode })));
    partiallySetUserPreferencesForControlBar({demoMode});
  },
  setDisplayLessonEndsWithVideoModal: (displayLessonEndsWithVideoModal: boolean) => { store.dispatch((lessonPlayerSlice.actions.displayLessonEndsWithVideoModalReducer({ displayLessonEndsWithVideoModal }))); },
  setDisplayNewSectionModalForLesson: (displayNewSectionModalForLesson?: number) => { store.dispatch((lessonPlayerSlice.actions.displayNewSectionModalForLessonReducer({ displayNewSectionModalForLesson }))); },
  setDisplayRhythmCounts: (isVisible: boolean) => {store.dispatch((lessonPlayerSlice.actions.displayRhythmCountsReducer({isVisible})))},
  setDisplayScoreModal: (displayScoreModal: boolean) => { store.dispatch((lessonPlayerSlice.actions.displayScoreModalReducer({ displayScoreModal }))); },
  setDisplaySectionEndModal: (displaySectionEndModal: boolean) => { store.dispatch((lessonPlayerSlice.actions.displaySectionEndModalReducer({ displaySectionEndModal }))); },
  setDisplaySectionStartModal: (displaySectionStartModal: boolean) => { store.dispatch((lessonPlayerSlice.actions.displaySectionStartModalReducer({ displaySectionStartModal }))); },
  setForceSideVideo: (forceSideVideo: boolean) => { store.dispatch((lessonPlayerSlice.actions.lessonPlayerSettingsForceSideVideoReducer({ forceSideVideo }))); },
  setForceTopVideo: (forceTopVideo: boolean) => { store.dispatch((lessonPlayerSlice.actions.lessonPlayerSettingsForceTopVideoReducer({ forceTopVideo }))); },
  setFretboardIsVisible: (isVisible: boolean) => { store.dispatch((lessonPlayerSlice.actions.fretboardVisibleReducer({ isVisible }))); },
  setHidePlayheadWhilePlaying: (hidePlayhead: boolean) => { store.dispatch(((lessonPlayerSlice.actions.hidePlayheadWhilePlayingReducer({ hidePlayhead })))); },
  setHighlightCurrentMeasure: (highlightCurrentMeasure: boolean) => { store.dispatch(((lessonPlayerSlice.actions.highlightCurrentMeasureReducer({ highlightCurrentMeasure })))); },
  setIsControlBarIsPartiallyHidden: (isControlBarPartiallyHidden: boolean) => { store.dispatch((lessonPlayerSlice.actions.isControlBarPartiallyHiddenReducer({ isControlBarPartiallyHidden }))); },
  setIsCountInPlaying: (isCountInPlaying: boolean) => { store.dispatch((lessonPlayerSlice.actions.isCountInPlayingReducer({ isCountInPlaying }))); },
  setIsDemoLeftHandSoundEnabled: (isDemoLeftHandSoundEnabled: boolean) => {
    partiallySetUserPreferencesForControlBar({isDemoLeftHandSoundEnabled});
    store.dispatch((lessonPlayerSlice.actions.isDemoLeftHandSoundEnabledReducer({ isDemoLeftHandSoundEnabled })));
  },
  setIsDemoRightHandSoundEnabled: (isDemoRightHandSoundEnabled: boolean) => {
    partiallySetUserPreferencesForControlBar({isDemoRightHandSoundEnabled});
    store.dispatch((lessonPlayerSlice.actions.isDemoRightHandSoundEnabledReducer({ isDemoRightHandSoundEnabled })));
  },
  setIsDemoSelectorDropdownOpen: (isDemoDropdownSelectorOpen: boolean) => { store.dispatch((lessonPlayerSlice.actions.isDemoSelectorDropdownOpenReducer({ isDemoDropdownSelectorOpen }))); },
  setDemoSoundPromptIsAllowedByUser: (isAllowed: boolean) => {
    localStorageService.setJsonItem<boolean>(storageKeys.lessonPlayer_demoSoundPrompt_isAllowedByUser, isAllowed);
    store.dispatch((lessonPlayerSlice.actions.isDemoSoundPromptAllowedByUserReducer({ isAllowed })));
  },
  setDemoSoundActivatedPromptIsAllowedByUser: (isAllowed: boolean) => {
    localStorageService.setJsonItem<boolean>(storageKeys.lessonPlayer_demoSoundActivatedPrompt_isAllowedByUser, isAllowed);
    store.dispatch((lessonPlayerSlice.actions.isDemoSoundActivatedPromptAllowedByUserReducer({ isAllowed })));
  },
  setIsInstantFeedbackEnabled: (isEnabled: boolean) => {
    partiallySetUserPreferencesForControlBar({isInstantFeedbackEnabled: isEnabled});
    store.dispatch((lessonPlayerSlice.actions.isInstantFeedbackEnabledReducer({ isEnabled })));
  },
  setIsLessonLoading: (isLessonLoading: boolean) => { store.dispatch((lessonPlayerSlice.actions.isLessonLoadingReducer({ isLessonLoading }))); },
  setIsLoopEnabled: (isLoopEnabled: boolean) => { store.dispatch((lessonPlayerSlice.actions.loopEnabledReducer({ isLoopEnabled }))); },
  setIsMidiConnected: (isConnected: boolean) => { store.dispatch((lessonPlayerSlice.actions.isMidiConnectedReducer({ isConnected }))); },
  setIsPlaybackEnabled: (isPlaybackEnabled: boolean) => { store.dispatch((lessonPlayerSlice.actions.isPlaybackEnabledReducer({ isPlaybackEnabled }))); },
  setIsPlaying: (isPlaying: boolean) => { store.dispatch((lessonPlayerSlice.actions.isPlayingReducer({ isPlaying }))); },
  setIsProgressBarShowing: (isProgressBarShown: boolean) => { store.dispatch((lessonPlayerSlice.actions.isProgressBarShownReducer({ isProgressBarShown }))); },
  setIsSegmentSelectorDropdownOpen: (isSegmentDropdownSelectorOpen: boolean) => { store.dispatch((lessonPlayerSlice.actions.isSegmentSelectorDropdownOpenReducer({ isSegmentDropdownSelectorOpen }))); },
  setIsSettingsSelectorDropdownOpen: (isSettingsSelectorDropdownOpen: boolean) => { store.dispatch((lessonPlayerSlice.actions.isSettingsSelectorDropdownOpenReducer({ isSettingsSelectorDropdownOpen }))); },
  setSwitchToPitchDetectionPromptIsAllowedByUser: (isAllowed: boolean) => {
    localStorageService.setJsonItem<boolean>(storageKeys.lessonPlayer_switchToPitchDetectionPrompt_isAllowedByUser, isAllowed);
    store.dispatch((lessonPlayerSlice.actions.isSwitchToPitchDetectionPromptAllowedByUserReducer({ isAllowed })));
  },
  setIsTempoSelectorDropdownOpen: (isTempoSelectorDropdownOpen: boolean) => { store.dispatch((lessonPlayerSlice.actions.isTempoSelectorDropdownOpenReducer({ isTempoSelectorDropdownOpen }))); },
  setLastTensorFlowBackendEngineInitialized: (lastTensorFlowBackendEngineInitialized: string) => { store.dispatch((lessonPlayerSlice.actions.lastTensorFlowBackendEngineInitializedReducer({ lastTensorFlowBackendEngineInitialized }))); },
  setLessonLoaded: (lesson?: Lesson) => { store.dispatch((lessonPlayerSlice.actions.lessonLoadedReducer({ lesson }))); },
  setMetricsSegmentsOpenedCount: (count: number) => {
    localStorageService.setJsonItem<number>(storageKeys.lessonPlayer_metrics_segmentOpenedCount, count);
    store.dispatch((lessonPlayerSlice.actions.metricsScoreDisplayCountReducer({ count })));
  },
  setPageCount: (pageCount: number) => { store.dispatch((lessonPlayerSlice.actions.pageCountReducer({pageCount}))) },
  setRecordingStartTimeInMs: (recordingStartTimeInMs: number) => { store.dispatch((lessonPlayerSlice.actions.recordingStartTimeInMsReducer({ recordingStartTimeInMs }))); },
  setReferrer: (referrer: string) => { store.dispatch((lessonPlayerSlice.actions.referrerReducer({ referrer }))); },
  setScoreModalViewModel: (scoreModalViewModel?: IScoreModal) => { store.dispatch((lessonPlayerSlice.actions.scoreModalViewModelReducer({ scoreModalViewModel }))); },
  setScoringStartTimeInMs: (scoringStartTimeInMs: number) => { store.dispatch((lessonPlayerSlice.actions.scoringStartTimeInMsReducer({ scoringStartTimeInMs }))); },
  setSegment: (segment?: Segment) => { store.dispatch((lessonPlayerSlice.actions.segmentReducer({ segment }))); },
  setSegmentSoundSliceIFrameUrl: (iframeUrl: string) => { store.dispatch((lessonPlayerSlice.actions.segmentSoundSliceIFrameUrlReducer({ iframeUrl }))); },
  setSegments: (segments: Segment[]) => { store.dispatch((lessonPlayerSlice.actions.segmentsReducer({ segments }))); },
  setSelectedNotationLayout: (layout: number) => {
    store.dispatch((lessonPlayerSlice.actions.selectedNotationLayoutReducer({ layout })));
    partiallySetUserPreferencesForControlBar({selectedNotationLayout: layout});
  },
  setShouldHideSegmentDescription: (shouldHideSegmentDescription: boolean) => {
    store.dispatch((lessonPlayerSlice.actions.shouldHideSegmentDescriptionReducer({ shouldHideSegmentDescription })));
  },
  setShowPermissionDeniedModal: (show: boolean) => { store.dispatch((lessonPlayerSlice.actions.showPermissionDeniedModalReducer({ show }))); },
  setShouldPlayWhenSSReady: (shouldPlayWhenSSReady: boolean) => { store.dispatch((lessonPlayerSlice.actions.shouldPlayWhenSSReadyReducer({ shouldPlayWhenSSReady }))); },
  setSpacingForActiveLesson: (spacing: number | undefined) => {
    // Update local storage
    const lessonSpacing = lessonPlayerStore.getAllLessonSpacing();
    const lessonId = lessonPlayerStore.getActiveLessonId();
    if (lessonId) {
      let newLessonSpacing;
      if (spacing === undefined) {
        newLessonSpacing = arrayRemove({
          array: lessonSpacing,
          item: {lessonId, spacing: 0},
          compareFn: (a, b) => a.lessonId === b.lessonId,
        });
      } else {
        newLessonSpacing = arrayUpsert<ILessonSpacing>({
          array: lessonSpacing,
          item: {lessonId, spacing},
          compareFn: (a, b) => a.lessonId === b.lessonId,
        });
        const minifiedLessonSpacing = minifyLessonSpacing(newLessonSpacing);
        localStorageService.setJsonItem<IMinifiedLessonSpacing[]>(storageKeys.lessonPlayer_notationSpacing, minifiedLessonSpacing);
      }
      lessonPlayerStore.setAllLessonSpacing(newLessonSpacing);
    }

    // Update redux
    store.dispatch((lessonPlayerSlice.actions.spacingForActiveLessonReducer({ spacing })));
  },
  setSpeed: (speed: number) => {
    store.dispatch((lessonPlayerSlice.actions.speedReducer({ speed })));
    partiallySetUserPreferencesForControlBar({speedPercent: speed});
  },
  setTempo: (tempo: number) => { store.dispatch((lessonPlayerSlice.actions.tempoReducer({ tempo }))); },
  // No setter is provided for the transcription status, since setting the transcription progress percentage will also set the transcription status. The two are coupled.
  setTranscriptionProgressPercentage: (percent: string) => { store.dispatch((lessonPlayerSlice.actions.transcriptionProgressPercentageReducer({ percent }))); },
  setZoom: (zoom: number): number => {
    const safeZoom = calculateSafeRangeZoomValue(zoom);
    store.dispatch((lessonPlayerSlice.actions.zoomReducer({ zoom: safeZoom })));
    partiallySetUserPreferencesForControlBar({zoomAmount: safeZoom});
    return safeZoom;
  },

  // Getters
  getActiveLessonId: (): number | undefined => { return lessonPlayerSlice.selectors.activeLessonIdSelector(store.getState()); },
  getAppliedNotationLayout: () : NotationLayoutEnum => { return lessonPlayerSlice.selectors.appliedNotationLayoutSelector(store.getState()); },
  getCanShowDemoSoundActivatedPrompt: (): boolean => { return lessonPlayerSlice.selectors.canShowDemoSoundActivatedPromptSelector(store.getState()); },
  getCanShowKeyboardConnectedModal: (): boolean => { return lessonPlayerSlice.selectors.canShowKeyboardConnectedModalSelector(store.getState()); },
  getCanShowKeyboardDisconnectedModal: (): boolean => { return lessonPlayerSlice.selectors.canShowKeyboardDisconnectedModalSelector(store.getState()); },
  getCanShowSwitchToPitchDetectionPrompt: (): boolean => { return lessonPlayerSlice.selectors.canShowSwitchToPitchDetectionPromptSelector(store.getState()); },
  getControlBarTogglesEnabledState: (): IControlBarTogglesEnabledState => { return lessonPlayerSlice.selectors.controlBarTogglesEnabledStateSelector(store.getState()); },
  getDefaultProportionalSpacing: (): number => { return 50; },
  getDemoMode: (): DemoMode => { return lessonPlayerSlice.selectors.demoModeSelector(store.getState()); },
  getDemoModeSelectOptions: (): ISelectOption<DemoMode>[] => { return lessonPlayerSlice.selectors.demoModeSelectOptionsSelector(store.getState()); },
  getDemoSoundPromptIsAllowedByUser: (): boolean => {
    const isAllowedByUserLocalValue = localStorageService.getOrCreateBooleanValue(storageKeys.lessonPlayer_demoSoundPrompt_isAllowedByUser, true);
    const isAllowedByUserReduxValue = lessonPlayerSlice.selectors.isDemoSoundPromptAllowedByUserSelector(store.getState());

    if (isAllowedByUserLocalValue !== isAllowedByUserReduxValue) {
      lessonPlayerStore.setDemoSoundPromptIsAllowedByUser(isAllowedByUserLocalValue);
    }

    return isAllowedByUserLocalValue;
  },
  getDemoSoundActivatedPromptIsAllowedByUser: (): boolean => {
    const isAllowedByUserLocalValue = localStorageService.getOrCreateBooleanValue(storageKeys.lessonPlayer_demoSoundActivatedPrompt_isAllowedByUser, true);
    const isAllowedByUserReduxValue = lessonPlayerSlice.selectors.isDemoSoundActivatedPromptAllowedByUserSelector(store.getState());

    if (isAllowedByUserLocalValue !== isAllowedByUserReduxValue) {
      lessonPlayerStore.setDemoSoundActivatedPromptIsAllowedByUser(isAllowedByUserLocalValue);
    }

    return isAllowedByUserLocalValue;
  },
  getDisplayLessonEndsWithVideoModal: (): boolean => { return lessonPlayerSlice.selectors.displayLessonEndsWithVideoModalSelector(store.getState()); },
  getDisplayNewSectionModalForLesson: (): number | undefined => { return lessonPlayerSlice.selectors.displayNewSectionModalForLessonSelector(store.getState()); },
  getDisplayScoreModal: (): boolean => { return lessonPlayerSlice.selectors.displayScoreModalSelector(store.getState()); },
  getDisplaySectionEndModal: (): boolean => { return lessonPlayerSlice.selectors.displaySectionEndModalSelector(store.getState()); },
  getDisplaySectionStartModal: (): boolean => { return lessonPlayerSlice.selectors.displaySectionStartModalSelector(store.getState()); },
  getHidePlayheadWhilePlaying: () : boolean => { return lessonPlayerSlice.selectors.hidePlayheadWhilePlayingSelector(store.getState()); },
  getHighlightCurrentMeasure: () : boolean => { return lessonPlayerSlice.selectors.highlightCurrentMeasureSelector(store.getState()); },
  getIsControlBarPartiallyHidden: (): boolean => { return lessonPlayerSlice.selectors.isControlBarPartiallyHiddenSelector(store.getState()); },
  getIsCountInPlaying: (): boolean => { return lessonPlayerSlice.selectors.isCountInPlayingSelector(store.getState()); },
  getIsDemoLeftHandSoundEnabled: (): boolean => {
    const isDemoLeftHandSoundEnabledFromRedux = lessonPlayerSlice.selectors.isDemoLeftHandSoundEnabledSelector(store.getState());
    const isDemoLeftHandSoundEnabledFromLocalStorage = getUserPreferencesForControlBar().isDemoLeftHandSoundEnabled ?? isDemoLeftHandSoundEnabledFromRedux;

    if (isDemoLeftHandSoundEnabledFromLocalStorage !== isDemoLeftHandSoundEnabledFromRedux) {
      store.dispatch((lessonPlayerSlice.actions.isDemoLeftHandSoundEnabledReducer({ isDemoLeftHandSoundEnabled: isDemoLeftHandSoundEnabledFromLocalStorage })));
    }

    return isDemoLeftHandSoundEnabledFromLocalStorage;
  },
  getIsDemoRightHandSoundEnabled: (): boolean => {
    const isDemoRightHandSoundEnabledFromRedux = lessonPlayerSlice.selectors.isDemoRightHandSoundEnabledSelector(store.getState());
    const isDemoRightHandSoundEnabledFromLocalStorage = getUserPreferencesForControlBar().isDemoRightHandSoundEnabled ?? isDemoRightHandSoundEnabledFromRedux;

    if (isDemoRightHandSoundEnabledFromLocalStorage !== isDemoRightHandSoundEnabledFromRedux) {
      store.dispatch((lessonPlayerSlice.actions.isDemoRightHandSoundEnabledReducer({ isDemoRightHandSoundEnabled: isDemoRightHandSoundEnabledFromLocalStorage })));
    }

    return isDemoRightHandSoundEnabledFromLocalStorage;
  },
  getDemoSoundMode: (): DemoMode => {
    const isDemoRightHandSoundEnabled = lessonPlayerStore.getIsDemoRightHandSoundEnabled();
    const isDemoLeftHandSoundEnabled = lessonPlayerStore.getIsDemoLeftHandSoundEnabled();

    if (isDemoRightHandSoundEnabled && isDemoLeftHandSoundEnabled) {
      return "both hands";
    } else if (isDemoRightHandSoundEnabled) {
      return "right hand";
    } else if (isDemoLeftHandSoundEnabled) {
      return "left hand";
    }

    return "off";
  },
  getIsDemoSelectorDropdownOpen: (): boolean => { return lessonPlayerSlice.selectors.isDemoSelectorDropdownOpenSelector(store.getState()); },
  getIsDemoSelectorEnabled: (): boolean => { return lessonPlayerSlice.selectors.isDemoSelectorEnabledSelector(store.getState()); },
  getIsFirstLessonOfSection: (): boolean => { return lessonPlayerSlice.selectors.isFirstLessonOfSectionSelector(store.getState()); },
  getIsInstantFeedbackEnabled: (): boolean => {
    const isInstantFeedbackEnabledFromRedux = lessonPlayerSlice.selectors.isInstantFeedBackEnabledSelector(store.getState()) ?? false
    const userPreferences = getUserPreferencesForControlBar();
    const isInstantFeedbackEnabledFromLocalStorage = userPreferences.isInstantFeedbackEnabled;

    if (isInstantFeedbackEnabledFromLocalStorage !== isInstantFeedbackEnabledFromRedux) {
      store.dispatch((lessonPlayerSlice.actions.isInstantFeedbackEnabledReducer({isEnabled: isInstantFeedbackEnabledFromLocalStorage})));
    }

    // Override user preference for followAlong and Play segment modes.
    const isSegmentTypeFollowAlong = lessonPlayerStore.getSegment()?.mode === SegmentModeEnum.followAlong;
    if (isSegmentTypeFollowAlong) {
      return true;
    }

    const isSegmentTypeVideo = lessonPlayerStore.getSegment()?.mode === SegmentModeEnum.play;
    if (isSegmentTypeVideo) {
      return false;
    }

    return isInstantFeedbackEnabledFromLocalStorage;
  },
  getIsMidiConnected: (): boolean => { return lessonPlayerSlice.selectors.isMidiConnectedSelector(store.getState()); },
  getIsPlaybackEnabled: (): boolean => { return lessonPlayerSlice.selectors.isPlaybackEnabledSelector(store.getState()); },
  getIsPlaying: (): boolean => { return lessonPlayerSlice.selectors.isPlayingSelector(store.getState()); },
  getIsProgressBarShown: (): boolean => { return lessonPlayerSlice.selectors.isProgressBarShownSelector(store.getState()); },
  getIsProportionalSpacingEnabled: (): boolean => {
    const spacingForActiveLesson = lessonPlayerStore.getSpacingForActiveLesson();
    return spacingForActiveLesson !== undefined;
  },
  getIsSegmentSelectorDropdownOpen: (): boolean => { return lessonPlayerSlice.selectors.isSegmentSelectorDropdownOpenSelector(store.getState()); },
  getIsSettingsSelectorDropdownOpen: (): boolean => { return lessonPlayerSlice.selectors.isSettingsSelectorDropdownOpenSelector(store.getState()); },
  getIsTempoSelectorDropdownOpen: (): boolean => { return lessonPlayerSlice.selectors.isTempoSelectorDropdownOpenSelector(store.getState()); },
  getLastTensorFlowBackendEngineInitialized: (): string => { return lessonPlayerSlice.selectors.lastTensorFlowBackendEngineInitializedSelector(store.getState()); },
  getLesson: (): Lesson | undefined => { return lessonPlayerSlice.selectors.lessonSelector(store.getState()); },
  getAllLessonSpacing: (): ILessonSpacing[] => {
    const spacingFromLocalStorage = expandLessonSpacing(localStorageService.getJsonItem<IMinifiedLessonSpacing[]>(storageKeys.lessonPlayer_notationSpacing));
    const spacingFromRedux = lessonPlayerSlice.selectors.lessonSpacingSelector(store.getState());

    if (spacingFromLocalStorage && JSON.stringify(spacingFromLocalStorage) !== JSON.stringify(spacingFromRedux)) {
      lessonPlayerStore.setAllLessonSpacing(spacingFromLocalStorage);
    }

    return spacingFromLocalStorage ?? [];
  },
  getLoopEnabled: (): boolean => { return lessonPlayerSlice.selectors.loopEnabledSelector(store.getState()); },
  getMetricsSegmentsOpenedCount: (): number => {
    const localStorageCount = localStorageService.getOrCreateNumberValue(storageKeys.lessonPlayer_metrics_segmentOpenedCount, 0);
    const reduxCount = lessonPlayerSlice.selectors.metricsSegmentsOpenedCountSelector(store.getState());

    if (localStorageCount !== reduxCount) {
      lessonPlayerStore.setMetricsSegmentsOpenedCount(localStorageCount);
    }

    return localStorageCount;
  },
  getPageCount: (): number => {return lessonPlayerSlice.selectors.pageCountSelector(store.getState())},
  getPlaybackControlState: (): IPlaybackControlState => { return lessonPlayerSlice.selectors.playbackControlStateSelector(store.getState()); },
  getRecordingStartTimeInMs: (): number | null => { return lessonPlayerSlice.selectors.recordingStartTimeInMsSelector(store.getState()); },
  getScoreModalViewModel: (): IScoreModal | undefined => { return lessonPlayerSlice.selectors.scoreModalViewModelSelector(store.getState()); },
  getScoringStartTimeInMs: (): number | null => { return lessonPlayerSlice.selectors.scoringStartTimeInMsSelector(store.getState()); },
  getSegment: (): Segment | undefined => { return lessonPlayerSlice.selectors.segmentSelector(store.getState()); },
  getSegments: (): Segment[] | undefined => { return lessonPlayerSlice.selectors.segmentsSelector(store.getState()); },
  getSelectedNotationLayout: () : NotationLayoutEnum => {
    // Local storage is the source of truth, if Redux and local storage are out of sync, update Redux.
    const notationLayoutFromLocalStorage = getUserPreferencesForControlBar().selectedNotationLayout;
    const notationLayoutFromRedux = lessonPlayerSlice.selectors.selectedNotationLayoutSelector(store.getState());

    if (notationLayoutFromLocalStorage !== notationLayoutFromRedux) {
      store.dispatch((lessonPlayerSlice.actions.selectedNotationLayoutReducer({ layout: notationLayoutFromLocalStorage })));
    }

    return notationLayoutFromLocalStorage;
  },
  getShouldHideSegmentDescription: (): boolean | undefined => { return lessonPlayerSlice.selectors.shouldHideSegmentDescriptionSelector(store.getState()); },
  getShowPermissionDeniedModal: (): boolean => { return lessonPlayerSlice.selectors.showPermissionDeniedModalSelector(store.getState()); },
  getShouldPlayWhenSSReady: (): boolean => { return lessonPlayerSlice.selectors.shouldPlayWhenSSReadySelector(store.getState()); },
  getSpacingForActiveLesson: (): number | undefined => { return lessonPlayerSlice.selectors.spacingForActiveLessonSelector(store.getState()); },
  getSpeed: (): number => {
    const speedLocalValue = getUserPreferencesForControlBar().speedPercent ?? 1;
    const speedReduxValue = lessonPlayerSlice.selectors.speedSelector(store.getState());

    if (speedLocalValue !== speedReduxValue) {
      lessonPlayerStore.setSpeed(speedLocalValue);
    }

    return speedLocalValue;
  },
  getSwitchToPitchDetectionPromptIsAllowedByUser: (): boolean => {
    const isAllowedByUserLocalValue = localStorageService.getOrCreateBooleanValue(storageKeys.lessonPlayer_switchToPitchDetectionPrompt_isAllowedByUser, true);
    const isAllowedByUserReduxValue = lessonPlayerSlice.selectors.isSwitchToPitchDetectionPromptAllowedByUserSelector(store.getState());

    if (isAllowedByUserLocalValue !== isAllowedByUserReduxValue) {
      lessonPlayerStore.setSwitchToPitchDetectionPromptIsAllowedByUser(isAllowedByUserLocalValue);
    }

    return isAllowedByUserLocalValue;
  },
  getTranscriptionProgressPercentage: (): string => { return lessonPlayerSlice.selectors.transcriptionProgressPercentageSelector(store.getState()); },
  getTranscriptionStatus: (): ILessonPlayerStore["transcriptionStatus"] => { return lessonPlayerSlice.selectors.transcriptionStatusSelector(store.getState()); },
  getIsPitchDetectionEnabled: (): boolean => {
    const isMicrophoneAvailable = microphoneStore.getIsAvailable();
    const isFeedbackEnabled = lessonPlayerStore.getIsInstantFeedbackEnabled();
    const isDemoSoundEnabled = lessonPlayerStore.getIsDemoRightHandSoundEnabled() || lessonPlayerStore.getIsDemoLeftHandSoundEnabled();
    const isMidiDeviceConnected = settingsStore.getIsMidiDeviceConnected();
    const isLoopEnabled = lessonPlayerStore.getLoopEnabled();
    let isInAVideoOrFollowAlong = false;
    const segment = lessonPlayerStore.getSegment();
    if (segment) {
      isInAVideoOrFollowAlong = segment.mode === SegmentModeEnum.play || segment.mode === SegmentModeEnum.followAlong;
    }

    const isEnabled = !isMidiDeviceConnected && !isDemoSoundEnabled && isFeedbackEnabled && !isLoopEnabled && isMicrophoneAvailable && !isInAVideoOrFollowAlong;
    return isEnabled;
  },
};

/**
 * Takes a zoom value and returns an adjusted value to ensure the zoom value is within the safe range.
 *
 * @param zoom - The current zoom value.
 * @returns The zoom value clamped within the range of minZoom and maxZoom.
 */
function calculateSafeRangeZoomValue(zoom: number){
  if (zoom < minimumSafeZoomValue) {
    return minimumSafeZoomValue;
  }

  if (zoom > maximumSafeZoomValue) {
    return maximumSafeZoomValue;
  }

  return zoom;
}

function minifyLessonSpacing(lessonSpacing: ILessonSpacing[]): IMinifiedLessonSpacing[] {
  const minifiedLessonSpacing: IMinifiedLessonSpacing[] = lessonSpacing.map((spacing) => {
    return {
      l: spacing.lessonId,
      s: spacing.spacing,
    };
  });

  return minifiedLessonSpacing;
}

function expandLessonSpacing(minifiedLessonSpacing: IMinifiedLessonSpacing[] | undefined): ILessonSpacing[] | undefined {
  if (!minifiedLessonSpacing) {
    return undefined;
  }

  const lessonSpacing: ILessonSpacing[] = minifiedLessonSpacing.map((spacing) => {
    return {
      lessonId: spacing.l,
      spacing: spacing.s,
    };
  });

  return lessonSpacing;
}
